import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import * as d3 from 'd3';
import * as L from 'leaflet';
import _ from 'lodash';

import { User } from '../../../models/Core/User';
import countryCoordinates from './CountryCoordinates';

const MAX_RADIUS_MARKER = 25;
const MIN_RADIUS_MARKER = 8;

@Component({
  selector: 'app-user-location-filter',
  templateUrl: './user-location-filter.component.html',
  styleUrls: ['./user-location-filter.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class UserLocationFilterComponent implements OnInit, OnDestroy {
  options: object;
  markers: any[] = [];
  map: any;
  @Input() users: User[] = [];
  @Output() updatedFilter = new EventEmitter();

  _hoveredUser: User;
  _selectedUser: User;
  _filteredUsers: User[];

  dropdownList: any;
  selectedItems: any;
  selectedCountries: object = {};

  disabledCountries;
  filteredCountries;
  selectedCountriesList: any[] = [];
  allCountries;

  @Input() set hoveredUser(user: User) {
    this._hoveredUser = user;
    if (this.users && this._filteredUsers) {
      this.initializeCountries(this.users, this._filteredUsers);
      this.updateMarkers();
    }
  }

  @Input() set filteredUsers(users: User[]) {
    this._filteredUsers = users;
    if (this.users && this._filteredUsers) {
      this.initializeCountries(this.users, this._filteredUsers);
      this.updateMarkers();
      this.initializeDropdownList();
    }
  }

  @Input() set selectedUser(user: User) {
    this._selectedUser = user;
    this.updateMarkers();
  }

  getCountryForUser(user: User): string {
    const location = user.location;
    if (location && location.country) {
      return location.country;
    }
    return null;
  }

  initializeCountries(allUsers, filteredUsers) {
    const visibleUsers = filteredUsers;
    this.allCountries = this.getCountriesForUsers(allUsers);
    this.filteredCountries = this.getCountriesForUsers(visibleUsers);
  }

  getCountriesForUsers(users) {
    const countries = [];
    for (const user of users) {
      if (user.location && user.location.country) {
        countries.push(user.location.country);
      }
    }

    return _.uniq(countries);
  }

  getUsersByCountry() {
    const usersByCountry = new Map();

    for (const user of this.users) {
      const country = this.getCountryForUser(user);
      if (country) {
        if (usersByCountry.has(country)) {
          usersByCountry.get(country).push(user);
        } else {
          usersByCountry.set(country, [user]);
        }
      }
    }

    return usersByCountry;
  }

  // returns if user not in filter (=> cannot select)
  isFilteredUser(user) {
    return (
      this._filteredUsers
        .map(filteredUser => filteredUser._id)
        .indexOf(user._id) >= 0
    );
  }

  updateMarkers() {
    const markers = [];

    const usersByCountry = this.getUsersByCountry();
    const maxPeoplePerCoordinate = Math.max(
      ...Array.from(usersByCountry.values()).map(users => users.length),
    );

    const scaleCoordinateMarker = d3
      .scaleLinear()
      .domain([1, maxPeoplePerCoordinate])
      .range([MIN_RADIUS_MARKER, MAX_RADIUS_MARKER]);

    usersByCountry.forEach((users, country) => {
      const coord = this.getCoordinateForCountry(country);
      if (coord) {
        let fillColor;
        let strokeColor;

        // cannot be selected because of other filter masks
        const disabledUsers = users.filter(user => !this.isFilteredUser(user));

        // can be selected
        const filteredUsers = users.filter(user => this.isFilteredUser(user));

        if (disabledUsers.length > 0) {
          fillColor = '#e2e2e2';
          strokeColor = '#ccc';
          const disabledMarker = L.circleMarker(
            { lat: coord[0], lng: coord[1] },
            {
              radius: scaleCoordinateMarker(users.length),
              fillColor: fillColor,
              fillOpacity: 0.6,
              weight: 3,
              color: strokeColor,
            },
          );

          markers.push(disabledMarker);
        }

        if (filteredUsers.length > 0) {
          if (!this.selectedCountries[country]) {
            fillColor = 'rgba(17,123,194,0.81)';
            strokeColor = 'rgb(13, 123, 186)';
          } else {
            strokeColor = '#59a947';
            fillColor = '#7DC855';
          }

          const marker = L.circleMarker(
            { lat: coord[0], lng: coord[1] },
            {
              radius: scaleCoordinateMarker(filteredUsers.length),
              fillColor: fillColor,
              fillOpacity: 0.6,
              weight: 3,
              color: strokeColor,
            },
          );
          marker.on('click', () => this.toggleCountry(country));
          markers.push(marker);
        }
      }
    });

    this.markers = markers;
  }

  getFilteredUsers() {
    return this._filteredUsers;
  }

  toggleCountry(country) {
    if (country in this.selectedCountries) {
      this.selectedCountries[country] = !this.selectedCountries[country];
    }

    this.selectedCountriesList = [];
    for (const _country of _.keys(this.selectedCountries)) {
      if (this.selectedCountries[_country]) {
        this.selectedCountriesList.push(_country);
      }
    }

    this.updateFilter();
    this.updateMarkers();

    if (!this.changeDetectorRef['destroyed']) {
      this.changeDetectorRef.detectChanges();
    }
  }

  getCoordinateForCountry(country) {
    const countryObject = _.find(countryCoordinates, { name: country });
    if (countryObject) {
      return countryObject['latlng'];
    } else {
      return null;
    }
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    this.options = {
      layers: [
        L.tileLayer('http://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 20,
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }),
      ],
      zoom: 1,
      maxZoom: 4,
      minZoom: 1,
      center: L.latLng(41.015137, 28.97953),
    };
  }

  onMapReady(map) {
    this.map = map;
    const group = L.featureGroup(this.markers);
    this.map.fitBounds(group.getBounds());
  }

  ngOnInit() {
    this.initializeCountries(this.users, this._filteredUsers);
    this.initializeDropdownList();

    this.selectedCountries = {};
    for (const country of this.allCountries) {
      this.selectedCountries[country] = false;
    }

    this.updateMarkers();
    this.updateSelectedItems();
  }

  ngOnDestroy(): void {
    this.changeDetectorRef.detach();
  }

  updateSelectedItems() {
    this.selectedItems = this.dropdownList.filter(
      item => this.selectedCountries[item.item_text],
    );
  }

  onSelect(item) {
    this.selectedCountries[item.item_text] = true;
    this.updateMarkers();
    this.updateFilter();
  }

  updateFilter() {
    const countries = _.keys(this.selectedCountries).filter(
      country => this.selectedCountries[country],
    );
    this.updatedFilter.emit(countries);
  }

  onSelectionChanged(selectedCountriesList) {
    for (const country of _.keys(this.selectedCountries)) {
      this.selectedCountries[country] = false;
    }

    for (const country of selectedCountriesList) {
      this.selectedCountries[country] = true;
    }

    this.updateFilter();
  }

  isFilteredCountry(country) {
    return this.filteredCountries.indexOf(country) >= 0;
  }

  isSelectedCountry(country) {
    return this.selectedCountries[country];
  }

  initializeDropdownList() {
    this.dropdownList = this.allCountries
      .filter(
        country =>
          this.isFilteredCountry(country) || this.isSelectedCountry(country),
      )
      .filter(country => this.getCoordinateForCountry(country))
      .map(country => {
        return { id: country, name: country };
      });
  }
}
